import { Component, EventEmitter, Input, Output } from '@angular/core';
import { RecipeModalSortOptions } from '@app/mpp/model/modal-values';
import { PlatformEnum } from '@panamax/app-state';
import { BaseDualPaneModalContainerComponent } from '@shared/components/modal-with-nav-inline/dual-pane-container/base-dual-pane-modal-container/base-dual-pane-modal-container.component';
import {
  dualPaneDefaultVerifySave,
  getSearchableObservable$,
  multiSelectSearchDefault,
  singleSelectRadioSearchDefault,
  updateRightPaneWithValue,
} from '@shared/components/modal-with-nav-inline/dual-pane-container/Helpers/dual-pane-helper-functions';
import {
  DualPaneView,
  MultiSelectItems,
} from '@shared/constants/dual-pane.enum';
import {
  BasicListItem,
  DualPaneFlagsConfig,
  DualPaneFunctionsConfig,
  DualPaneLabelsConfig,
  DualPaneViewModel,
  LeftPaneInfo,
  RightPaneInfo,
} from '@shared/models/dual-pane-model';
import { BehaviorSubject, of } from 'rxjs';

@Component({
  selector: 'app-sort-filter-mpp-modal-container',
  templateUrl: './sort-filter-mpp-modal-container.component.html',
  styleUrls: ['./sort-filter-mpp-modal-container.component.scss'],
})
export class SortFilterMppModalContainerComponent extends BaseDualPaneModalContainerComponent {
  // we will use these values in order to set default or existing data
  @Input() exisitingGroupFilter: string[];
  @Input() existingSort: RecipeModalSortOptions;
  @Input() isActive: boolean;
  @Input() groups: string[];

  @Output() clearAll = new EventEmitter<void>();

  mutatingSortOptions = new BehaviorSubject<any[]>([]);

  sortOptions = (existingSelection: RecipeModalSortOptions) => [
    {
      label: 'i18n.menuProfitPro.filterModal.sortRecipeAZ',
      value: RecipeModalSortOptions.RecipeAZ,
      selected: existingSelection === RecipeModalSortOptions.RecipeAZ,
      type: MultiSelectItems.basic,
    },
    {
      label: 'i18n.menuProfitPro.filterModal.sortRecipeZA',
      value: RecipeModalSortOptions.RecipeZA,
      selected: existingSelection === RecipeModalSortOptions.RecipeZA,
      type: MultiSelectItems.basic,
    },
    {
      label: 'i18n.menuProfitPro.filterModal.sortGroupAZ',
      value: RecipeModalSortOptions.GroupAZ,
      selected: existingSelection === RecipeModalSortOptions.GroupAZ,
      type: MultiSelectItems.basic,
    },
    {
      label: 'i18n.menuProfitPro.filterModal.sortGroupZA',
      value: RecipeModalSortOptions.GroupZA,
      selected: existingSelection === RecipeModalSortOptions.GroupZA,
      type: MultiSelectItems.basic,
    },
    {
      label: 'i18n.menuProfitPro.filterModal.sortServingSize',
      value:
        existingSelection === RecipeModalSortOptions.Serving90
          ? RecipeModalSortOptions.Serving90
          : RecipeModalSortOptions.Serving09,
      selected:
        existingSelection === RecipeModalSortOptions.Serving09 ||
        existingSelection === RecipeModalSortOptions.Serving90,
      type: MultiSelectItems.directional,
      sortable: {
        showNothing:
          existingSelection !== RecipeModalSortOptions.Serving09 &&
          existingSelection !== RecipeModalSortOptions.Serving90,
        showASC: existingSelection === RecipeModalSortOptions.Serving09,
        showDSC: existingSelection === RecipeModalSortOptions.Serving90,
      },
    },
    {
      label: 'i18n.menuProfitPro.filterModal.sortCostServing',
      value:
        existingSelection === RecipeModalSortOptions.CostServ90
          ? RecipeModalSortOptions.CostServ90
          : RecipeModalSortOptions.CostServ09,
      selected:
        existingSelection === RecipeModalSortOptions.CostServ09 ||
        existingSelection === RecipeModalSortOptions.CostServ90,
      type: MultiSelectItems.directional,
      sortable: {
        showNothing:
          existingSelection !== RecipeModalSortOptions.CostServ09 &&
          existingSelection !== RecipeModalSortOptions.CostServ90,
        showASC: existingSelection === RecipeModalSortOptions.CostServ09,
        showDSC: existingSelection === RecipeModalSortOptions.CostServ90,
      },
    },
  ];

  filterStatus = (isActive: boolean) => [
    {
      label: 'i18n.menuProfitPro.filterModal.active',
      value: true,
      selected: isActive,
      type: MultiSelectItems.basic,
    },
    {
      label: 'i18n.menuProfitPro.filterModal.archived',
      value: false,
      selected: !isActive,
      type: MultiSelectItems.basic,
    },
  ];

  filterGroup = (existingGroups: string[], selectedGroups: any[]) => {
    let groupsToCheck = [];
    // can be just strings or full on objects
    if (selectedGroups.length > 0 && selectedGroups[0]?.value !== undefined) {
      groupsToCheck = selectedGroups.map(item => item.value);
    } else if (selectedGroups.length > 0) {
      groupsToCheck = selectedGroups;
    }
    const result = existingGroups.map(group => {
      return {
        label: group,
        value: group,
        selected: groupsToCheck.includes(group),
        type: MultiSelectItems.basic,
      };
    });
    return result;
  };

  convertToItems = (items: string[]) => {
    return items.map(item => {
      return {
        label: item,
        value: item,
        selected: true,
        type: MultiSelectItems.basic,
      };
    });
  };

  createFilterVM = () => {
    const labelConfig: DualPaneLabelsConfig = {
      dataCy: 'mpp-sort-filter',
      title:
        this.platformService.platformEnum.mobile ===
        this.platformService.platformType
          ? 'i18n.menuProfitPro.filterModal.titleMobile'
          : 'i18n.menuProfitPro.filterModal.titleDesktop',
      submitText: 'i18n.common.apply',
      backText: 'i18n.common.save',
      cancelText: 'i18n.common.clearAll',
    };
    const functionConfig: DualPaneFunctionsConfig = {
      intialDesktopTabletView: this.buildFilterGroupRightPane,
      handleSubmit: this.submitModalValues,
      verifySubmit: this.verifySubmission,
      overrideDismiss: this.handleClearAll,
    };

    const flagConfig: DualPaneFlagsConfig = {};

    const contentItems: BasicListItem[] = [
      {
        title: 'i18n.menuProfitPro.filterModal.filterHeader',
        label: 'i18n.menuProfitPro.filterModal.groupLabel',
        placeHolder: 'i18n.menuProfitPro.filterModal.groupSortPlaceholder',
        view: DualPaneView.multiSelectCheckbox,
        selected: this.platformService.platformType !== PlatformEnum.mobile,
        value: this.convertToItems(this.exisitingGroupFilter ?? []),
        subLabel: this.converGroupsStringsToLabel(
          this.exisitingGroupFilter ?? [],
        ),
        onSelectfunction: this.buildFilterGroupRightPane,
      },
      {
        label: 'i18n.menuProfitPro.filterModal.statusLabel',
        placeHolder: 'i18n.menuProfitPro.filterModal.statusActive',
        view: DualPaneView.searchableItemRadio,
        selected: false,
        value: this.isActive,
        subLabel: this.isActive
          ? 'i18n.menuProfitPro.filterModal.statusActive'
          : 'i18n.menuProfitPro.filterModal.statusArchived',
        onSelectfunction: this.buildFilterStatusRightPane,
      },
    ];

    if (
      this.platformService.platformEnum.mobile ===
      this.platformService.platformType
    ) {
      contentItems.unshift({
        title: 'i18n.menuProfitPro.filterModal.sortHeader',
        label: 'i18n.menuProfitPro.filterModal.sortLabel',
        placeHolder: 'i18n.menuProfitPro.filterModal.groupSortPlaceholder',
        view: DualPaneView.searchableItemRadio,
        selected: false,
        subLabel: this.convertStringToLabel(this.existingSort),
        value: this.existingSort,
        onSelectfunction: this.buildSortRightPane,
      });
    }

    this.buildBasePaneWithConfigs(
      labelConfig,
      functionConfig,
      flagConfig,
      contentItems,
    );
  };

  buildFilterGroupRightPane = (
    viewModel: DualPaneViewModel,
    paneSelected: string,
  ) => {
    const groupIndex = this.isMobile() ? 1 : 0;
    let val = [];
    if (viewModel?.leftView === undefined) {
      val = this.convertToItems(this.exisitingGroupFilter ?? []);
    } else {
      val = viewModel?.leftView?.content[groupIndex].value ?? [];
    }

    const listTypeModal = {
      view: DualPaneView.multiSelectCheckbox,
      title: 'i18n.menuProfitPro.filterModal.groupLabel',
      verifySaveButton: dualPaneDefaultVerifySave,
      searchPane: {
        hideSearch: true,
        onSearchFunction: multiSelectSearchDefault,
        searchBehavior: new BehaviorSubject<string>(''),
        searchKey: '',
        searchPlaceholder: '',
      },
      countPane: {
        primaryCount: val.length,
        hideSelectAll: true,
        selectableText: '',
      },
      itemsObservable$: of(this.filterGroup(this.groups, val)),
      handleItemSelection: this.groupSelection,
      overrideSaveOnNavigation: this.saveOverrideGroup,
      value: val,
    } as RightPaneInfo;
    listTypeModal.searchPane.searchableObservable$ =
      getSearchableObservable$(listTypeModal);
    updateRightPaneWithValue(viewModel, paneSelected, listTypeModal);
    return listTypeModal;
  };

  converGroupsStringsToLabel = (value: string[]) => {
    let label = '';
    if (value.length > 1) {
      label = 'selected: ' + value.length;
    } else if (value.length > 0) {
      label += value[0];
    }
    return label;
  };

  convertGroupsToLabel = (value: any[]) => {
    let label = '';
    if (value.length > 1) {
      label = 'selected: ' + value.length;
    } else if (value.length > 0) {
      label += value[0].value;
    }
    return label;
  };

  groupSelection = (
    left: LeftPaneInfo,
    platform: PlatformEnum,
    right: RightPaneInfo,
    value: any,
  ) => {
    const groupIndex = this.isMobile() ? 1 : 0;
    let label = this.convertGroupsToLabel(value);
    left.content[groupIndex] = {
      ...left.content[groupIndex],
      subLabel: label,
      value: value,
    };
    right.value = value;
  };

  saveOverrideGroup = (
    vm: DualPaneViewModel,
    item: BasicListItem,
    value: any,
  ) => {
    let label = '';
    if (value.length > 1) {
      label = 'selected: ' + value.length;
    } else if (value.length > 0) {
      label += value[0].value;
    }
    item.value = value;
    item.subLabel = label;
    return item;
  };

  buildFilterStatusRightPane = (
    viewModel: DualPaneViewModel,
    paneSelected: string,
  ) => {
    const statusIndex = this.isMobile() ? 2 : 1;
    let val = viewModel?.leftView?.content[statusIndex].value ?? true;
    const listTypeModal = {
      view: DualPaneView.searchableItemRadio,
      title: 'i18n.menuProfitPro.filterModal.statusLabel',
      verifySaveButton: dualPaneDefaultVerifySave,
      searchPane: {
        hideSearch: true,
        onSearchFunction: singleSelectRadioSearchDefault,
        searchBehavior: new BehaviorSubject<string>(''),
        searchKey: '',
        searchPlaceholder: '',
      },
      itemsObservable$: of(this.filterStatus(val)),
      handleItemSelection: this.statusSelection,
      overrideSaveOnNavigation: this.saveOverrideStatus,
    } as RightPaneInfo;
    listTypeModal.searchPane.searchableObservable$ =
      getSearchableObservable$(listTypeModal);
    updateRightPaneWithValue(viewModel, paneSelected, listTypeModal);
    return listTypeModal;
  };

  statusSelection = (
    left: LeftPaneInfo,
    platform: PlatformEnum,
    right: RightPaneInfo,
    value: any,
  ) => {
    const statusIndex = this.isMobile() ? 2 : 1;
    let label = 'i18n.menuProfitPro.filterModal.statusActive';
    // don't ask
    if (value === 'false') {
      label = 'i18n.menuProfitPro.filterModal.statusArchived';
    }
    left.content[statusIndex] = {
      ...left.content[statusIndex],
      subLabel: label,
      value: value,
    };
    right.value = value;
  };

  saveOverrideStatus = (
    vm: DualPaneViewModel,
    item: BasicListItem,
    value: any,
  ) => {
    let label = 'i18n.menuProfitPro.filterModal.statusActive';
    // don't ask
    if (value === 'false') {
      label = 'i18n.menuProfitPro.filterModal.statusArchived';
    }
    item.value = value;
    item.subLabel = label;
    return item;
  };

  buildSortRightPane = (viewModel: DualPaneViewModel, paneSelected: string) => {
    let val = viewModel?.leftView?.content[0].value;
    this.mutatingSortOptions.next(this.sortOptions(val));
    const listTypeModal = {
      view: DualPaneView.searchableItemRadio,
      title: 'i18n.menuProfitPro.filterModal.sortLabel',
      verifySaveButton: dualPaneDefaultVerifySave,
      searchPane: {
        hideSearch: true,
        onSearchFunction: singleSelectRadioSearchDefault,
        searchBehavior: new BehaviorSubject<string>(''),
        searchKey: '',
        searchPlaceholder: '',
      },
      itemsObservable$: this.mutatingSortOptions,
      handleItemSelection: this.positionSelection,
      overrideSaveOnNavigation: this.saveOverrideSort,
    } as RightPaneInfo;
    listTypeModal.searchPane.searchableObservable$ =
      getSearchableObservable$(listTypeModal);
    updateRightPaneWithValue(viewModel, paneSelected, listTypeModal);
    return listTypeModal;
  };

  convertStringToLabel = (value: any) => {
    let label = '';
    switch (value) {
      case RecipeModalSortOptions.RecipeAZ:
        label = 'i18n.menuProfitPro.filterModal.sortRecipeAZ';
        break;
      case RecipeModalSortOptions.RecipeZA:
        label = 'i18n.menuProfitPro.filterModal.sortRecipeZA';
        break;
      case RecipeModalSortOptions.GroupAZ:
        label = 'i18n.menuProfitPro.filterModal.sortGroupAZ';
        break;
      case RecipeModalSortOptions.GroupZA:
        label = 'i18n.menuProfitPro.filterModal.sortGroupZA';
        break;
      case RecipeModalSortOptions.Serving09:
        label = 'i18n.menuProfitPro.filterModal.servingASC';
        break;
      case RecipeModalSortOptions.Serving90:
        label = 'i18n.menuProfitPro.filterModal.servingDEC';
        break;
      case RecipeModalSortOptions.CostServ09:
        label = 'i18n.menuProfitPro.filterModal.costASC';
        break;
      case RecipeModalSortOptions.CostServ90:
        label = 'i18n.menuProfitPro.filterModal.costDEC';
        break;
    }
    return label;
  };

  positionSelection = (
    left: LeftPaneInfo,
    platform: PlatformEnum,
    right: RightPaneInfo,
    value: any,
  ) => {
    let valueToMutate = value;
    if (
      value === RecipeModalSortOptions.Serving09 ||
      value === RecipeModalSortOptions.Serving90
    ) {
      let item = this.mutatingSortOptions.value[4];
      if (!!item.sortable.showNothing) {
        valueToMutate = RecipeModalSortOptions.Serving09;
      } else if (!!item.sortable.showASC) {
        valueToMutate = RecipeModalSortOptions.Serving90;
      } else {
        valueToMutate = RecipeModalSortOptions.Serving09;
      }
    }

    if (
      value === RecipeModalSortOptions.CostServ09 ||
      value === RecipeModalSortOptions.CostServ90
    ) {
      let item = this.mutatingSortOptions.value[5];
      if (!!item.sortable.showNothing) {
        valueToMutate = RecipeModalSortOptions.CostServ09;
      } else if (!!item.sortable.showASC) {
        valueToMutate = RecipeModalSortOptions.CostServ90;
      } else {
        valueToMutate = RecipeModalSortOptions.CostServ09;
      }
    }

    this.mutatingSortOptions.next(this.sortOptions(valueToMutate));

    let label = this.convertStringToLabel(valueToMutate);

    left.content[0] = {
      ...left.content[0],
      subLabel: label,
      value: valueToMutate,
    };

    right.value = value;
  };

  saveOverrideSort = (
    vm: DualPaneViewModel,
    item: BasicListItem,
    value: any,
  ) => {
    item.value = value;
    item.subLabel = this.convertStringToLabel(value);
    return item;
  };

  verifySubmission = (vm: DualPaneViewModel) => {
    return true;
  };

  handleClearAll = () => {
    this.clearAll.emit();
    this.closeModal();
  };

  openModal() {
    this.createFilterVM();
    this.isModalOpen$.next(true);
  }

  closeModal = () => {
    this.isModalOpen$.next(false);
  };

  isMobile = () => {
    return (
      this.platformService.platformEnum.mobile ===
      this.platformService.platformType
    );
  };

  submitModalValues = async (vm: DualPaneViewModel) => {
    const submitMap = new Map<string, any | any[]>();
    const groupIndex = this.isMobile() ? 1 : 0;
    const statusIndex = this.isMobile() ? 2 : 1;
    const sortIndex = this.isMobile() ? 0 : 1;
    submitMap.set('group', vm.leftView.content[groupIndex].value);
    submitMap.set('status', vm.leftView.content[statusIndex].value);
    if (
      this.platformService.platformEnum.mobile ===
      this.platformService.platformType
    ) {
      submitMap.set('sort', vm.leftView.content[sortIndex].value);
    }
    this.submit$.next(submitMap);
    this.closeModalAndSubmit();
  };

  closeModalAndSubmit() {
    this.isModalOpen$.next(false);
    if (this.submit$.value !== undefined) {
      this.submitEvent.emit(this.submit$.value);
    }
  }
}
